<template>
  <modal
    :name="title"
    :adaptive="true"
    height="auto"
    :width="500"
    transition="linear"
    @before-close="handleAddCoparentModalOnClosing"
  >
    <form
      name="addCoparentForm"
      class="fp-modal"
      novalidate
      @submit.prevent
      autocomplete="off"
    >

      <modals-header
        :modalNameProp="title"
        @hideModalProp="hideAddCoparentModal"
      >
      </modals-header>

      <hr class="v--modal-hr">
  
      <input autocomplete="off" name="hidden" type="text" class="hidden">
      
      <!--Step 1 - choosing Familipay type-->
      <div class="modal-step center" v-if="step1">
        <button type="button" class="action-btn" @click="chooseSolo">
          <span>{{ $lang.messages.familipaySolo }}</span>
        </button>
        <p>{{ $lang.messages.familipaySoloText1 }} <br><br> {{ $lang.messages.familipaySoloText2 }}</p>
    
        <p class="text-uppercase or">{{ $lang.messages.or }}</p>
    
        <button type="button" class="action-btn" @click="chooseDuo">
          <span>{{ $lang.messages.familipayDuo }}</span>
        </button>
        <p>{{ $lang.messages.familipayDuoText1 }}</p>
      </div>
      
      <div v-if="!step1">
        <!-- FIRST NAME -->
        <div class="part">
          <p class="label input-filter-label">
            {{ $lang.messages.firstName }}
            <span class="asterix">*</span>
          </p>
          <div class="input-wrapper input-field" :class="{'error-input' : errors.has('addCoparent.firstName')}">
            <input
              type="text"
              name="firstName"
              class="input"
              :placeholder="$lang.messages.enterCoparentFirstName"
              v-model="coparent.firstName"
              data-vv-scope="addCoparent"
              v-validate="'required|min:1|max:32'"
              autocomplete="no"
              @click.self="clearInputErrors($event)"
              @focus.self="clearInputErrors($event)"
              @blur="clearOthers($event)"
            />
            <img src="@/assets/images/validation_ico.png" alt="" class="icon error-icon" />
            <div
              v-show="errors.has('addCoparent.firstName')"
              class="error-tooltip"
              :class="{'hide-tooltip' : bluredProp !== 'firstName'}"
            >
              <div class="text">
                {{ errors.first('addCoparent.firstName') }}
              </div>
            </div>
          </div>
        </div>

        <!-- LAST NAME -->
        <div class="part">
          <p class="label input-filter-label">
            {{ $lang.messages.lastName }}
            <span class="asterix">*</span>
          </p>
          <div class="input-wrapper input-field" :class="{'error-input' : errors.has('addCoparent.lastName')}">
            <input
              type="text"
              name="lastName"
              class="input"
              :placeholder="$lang.messages.enterCoparentLastName"
              v-model="coparent.lastName"
              data-vv-scope="addCoparent"
              v-validate="'required|min:1|max:32'"
              autocomplete="no"
              @click.self="clearInputErrors($event)"
              @focus.self="clearInputErrors($event)"
              @blur="clearOthers($event)"
            />
            <img src="@/assets/images/validation_ico.png" alt="" class="icon error-icon" />
            <div
              v-show="errors.has('addCoparent.lastName')"
              class="error-tooltip"
              :class="{'hide-tooltip' : bluredProp !== 'lastName'}"
            >
              <div class="text">
                {{ errors.first('addCoparent.lastName') }}
              </div>
            </div>
          </div>
        </div>

        <!-- INVITE -->
        <div class="part" v-if="step2">
          <label class="checkbox-wrapper">
            {{ $lang.messages.sendDigitalChecks }}
            <input type="checkbox" class="checkbox-input" v-model="coparent.invited">
            <span class="checkmark"></span>
          </label>
          <p class="text">{{ $lang.messages.sendDigitalChecksText1 }}</p>
        </div>

        <!-- EMAIL -->
        <div class="part">
          <p class="label input-filter-label">
            {{ $lang.messages.email }}
            <span class="asterix" v-show="step3 || (step2 && coparent.invited)">*</span>
          </p>
          <div class="input-wrapper input-field" :class="{'error-input' : errors.has('addCoparent.email'), 'disabled' : !coparent.invited}">
            <input
              type="email"
              name="email"
              class="input"
              :placeholder="$lang.messages.enterCoparentEmail"
              v-model="coparent.email"
              data-vv-scope="addCoparent"
              v-validate="emailValidationRule"
              :tabindex="!coparent.invited ? -1 : 0"
              autocomplete="no"
              @click.self="clearInputErrors($event)"
              @focus.self="clearInputErrors($event)"
              @blur="clearOthers($event)"
            />
            <img src="@/assets/images/validation_ico.png" alt="" class="icon error-icon" />
            <div
                v-show="errors.has('addCoparent.email')"
                class="error-tooltip"
                :class="{'hide-tooltip' : bluredProp !== 'email'}"
            >
              <div class="text">
                {{ errors.first('addCoparent.email') }}
              </div>
            </div>
          </div>
        </div>
  
        <div class="part" v-if="step3">
          <p class="text">{{ $lang.messages.sendDigitalChecksText2 }}</p>
        </div>

        <div class="part mandatory-fields">
          <p class="label input-filter-label">
            <span class="asterix">*</span> {{ $lang.messages.mandatoryFields }}
          </p>
        </div>

        <!-- CLOSE MODAL-->
        <button
          type="button"
          class="generic-btn"
          @click="hideAddCoparentModal"
        >
          {{ $lang.messages.cancel }}
        </button>

        <!-- SUBMIT -->
        <f-p-button-loader :loading="loading" :text="addButton" @click.native="onAddCoparentFormSubmitted" ></f-p-button-loader>
      </div>
    </form>
  </modal>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import {
  ADD_COPARENT,
  EDIT_COPARENT,
  GET_REGISTRATION_PROGRESS
} from "@/store/action.types";
import { dateFormat, requestDateFormatUTC, requestDateFormat } from '@/mixins/dateFormat';
import Autocomplete from "@/components/common/Autocomplete";
import modalsHeader from "@/components/common/modals/ModalsHeader";
import FPDatepicker from '@/components/common/FPDatepicker';
import FPButtonLoader from "@/components/common/FPButtonLoader";
import STATUS_CODES from "@/common/statusCodes";

export default {
  name: "addCoparent",
  inject: ['$validator'],
  components: {
    Autocomplete,
    'modals-header': modalsHeader,
    FPDatepicker,
    'f-p-button-loader': FPButtonLoader
  },
  mixins: [
    dateFormat,
    requestDateFormatUTC,
    requestDateFormat
  ],
  data() {
    return {
      coparentActionType: '',
      coparent: {
        firstName: '',
        lastName: '',
        invited: true,
        email: '',
      },
      bluredProp: '',
      title: '',
      addButton: '',
      typeable: true,
      loading: false,
      step1: true,
      step2: false,
      step3: false
    };
  },
  computed: {
    emailValidationRule() {
      return this.coparent.invited ? 'required|min:1|max:64' : '';
    },
    ...mapGetters([
      "isAppLocked",
    ]),
  },
  watch: {
    "coparent.invited": function(value) {
      if (!value) {
        this.checkAnyInput();
        this.coparent.email = ''
      }
    }
  } ,
  methods: {
    ...mapActions({
      addCoparentAction: ADD_COPARENT,
      editCoparentAction: EDIT_COPARENT,
      getRegistrationProgressAction: GET_REGISTRATION_PROGRESS
    }),
    show(coparentActionTypeParam, coparentToAddParam) {
      this.coparentActionType = coparentActionTypeParam;
      this.coparent = coparentToAddParam;

      if (this.coparentActionType === 'add') {
        this.clearAddCoparentData();
        this.title = this.$lang.messages.addCoparentModal;
        this.addButton = this.$lang.messages.addCoparent;
      }
      else {
        if (coparentToAddParam.coparentType.toLowerCase() === 'solo') {
          this.chooseSolo();
        } else {
          this.chooseDuo();
        }
        this.title = this.$lang.messages.editCoparentModal;
        this.addButton = this.$lang.messages.save;
      }
      setTimeout(() => this.$modal.show(this.title), 0);
      this.errors.clear();
    },
    hideAddCoparentModal() {
      this.checkAnyInput();
      this.$parent.addCoparentModalOpened = false;
      this.$modal.hide(this.title);
    },
    /**
     * @description When user closes the modal send the emmited value to UserProfile
     * so we could turn on/off the registration tooltip
     */
    handleAddCoparentModalOnClosing() {
      this.$emit('handleAddCoparentModalOnClosing', false);
      this.resetSteps();
    },
    clearAddCoparentData() {
      this.coparent.firstName = '';
      this.coparent.lastName = '';
      this.coparent.invited = true;
      this.coparent.email = '';
      this.$validator.reset();
      this.checkAnyInput();
    },
    resetSteps() {
      this.step1 = true;
      this.step2 = false;
      this.step3 = false;
    },
    chooseSolo() {
      this.step1 = false;
      this.step2 = true;
      this.step3 = false;
    },
    chooseDuo() {
      this.step1 = false;
      this.step2 = false;
      this.step3 = true;
    },
    onAddCoparentFormSubmitted() {
      if (this.errors.items.length >= 1) {
        this.checkAnyInput();
        return
      }
      if (!this.loading) {
        this.$validator.validateAll('addCoparent').then(result => {
          this.checkAnyInput();
          if (result) {
            this.loading = true;
            if (this.coparentActionType === 'add') {
              const sendDigitalChecks = this.step2 ? this.coparent.invited : false;
              const invited = this.step3;
              const email = this.step2 ? (this.coparent.invited ? this.coparent.email : null) : this.coparent.email;
              this.addCoparentAction({
                firstName: this.coparent.firstName,
                lastName: this.coparent.lastName,
                invited: invited,
                sendDigitalChecks: sendDigitalChecks,
                email: email
              })
              .then(() => {
                // Call /account/setup/progress for registration wizard
                this.getRegistrationProgressAction()
              })
              .then(() => {
                this.$parent.loadUserProfile(true, false);
                this.$parent.$parent.loadCoparents();

                this.$snotify.success(this.$lang.messages.sn_coparentAdded, "SUCCESS");
              })
              .catch(error => {
                this.hideAddCoparentModal();
                if (error.response) {
                  if (error.response.status !== 406 && error.response.data.status.code !== STATUS_CODES.FP_405 && error.response.status !== 401 && error.response.status !== 402) {
                    this.$snotify.error(error.response.data.status.desc, "ERROR");
                  }
                }
              })
              .finally(() => {
                this.loading = false;
                this.hideAddCoparentModal();
              })
            }
            else if (this.coparentActionType === 'edit') {
              this.editCoparentAction({
                firstName: this.coparent.firstName,
                lastName: this.coparent.lastName,
                email: this.coparent.email === '' ? null : this.coparent.email,
                guid: this.coparent.guid
              })
              .then(() => {
                this.$snotify.success(this.$lang.messages.sn_coparentEdited, "SUCCESS");
                this.loading = false;
                if (!this.isAppLocked)
                  this.$root.$emit('reload-coparents');
                this.hideAddCoparentModal();
                this.$parent.loadUserProfile(false, false);
                this.$parent.$parent.loadCoparents();
              })
              .catch(error => {
                this.hideAddCoparentModal();
                if (error.response) {
                  if (error.response.status !== 406 && error.response.data.status.code !== STATUS_CODES.FP_405 && error.response.status !== 401 && error.response.status !== 402) {
                    this.$snotify.error(error.response.data.status.desc, "ERROR");
                  }
                }
              })
              .finally(() => this.loading = false)
            }
          }
        });
      }
    },
    clearInputErrors(e) {
      this.$validator.errors.remove('addCoparent.' + e.target.name)
    },
    checkAnyInput() {
      this.bluredProp = ''
    },
    clearOthers(e) {
      this.bluredProp = e.target.name;
      let errorMsg = this.bluredProp;
      if (!e.target.value || e.target.name === "") {
        if (errorMsg === 'firstName') {
          errorMsg = 'First Name'
        } else {
          errorMsg ='Last Name';
        }
        this.$validator.errors.add({
          field: this.bluredProp,
          scope: 'addCoparent',
          msg: `${this.$lang.messages.vee_the} ${errorMsg} ${this.$lang.messages.vee_requiredField}.`
        })
      }
    },
    /* DISABLED - need to find solution for vee-validate inside modals */
    // Update the input value we are sending to FPInputTextValidate
    // inputTextValueFirstName (val) {
    //   this.coparentToAdd.firstName = val
    // },
    // inputTextValueLastName (val) {
    //   this.coparentToAdd.lastName = val
    // }
  }
};
</script>
